import { cn, Input, Label, Spinner } from '@crm/ui';
import { useQuery } from '@tanstack/react-query';
import { Check, Filter, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@/hooks';
import { apiClient } from '@/lib/api-client';

export interface AutocompleteOption {
  id: string;
  label: string;
  /** Linha secundaria, ex.: preco e duracao do servico. */
  hint?: React.ReactNode;
  /** A linha original do endpoint, para quem precisa de mais que o rotulo. */
  data?: Record<string, unknown>;
}

type Size = 'sm' | 'md' | 'lg';

/**
 * `field` e o campo de formulario; `search` e a busca cinza de barra lateral,
 * com lupa e cantos retos; `filter` e a mesma busca, mas o que ela faz e
 * filtrar uma lista que ja esta na tela - dai o funil no lugar da lupa.
 */
type Variant = 'field' | 'search' | 'filter';

// O estilo do campo e global (ver `Input`); aqui a variante so abre espaco
// para o icone.
const VARIANT_CLASS: Record<Variant, string> = {
  field: '',
  search: 'pl-9',
  filter: 'pl-9',
};

const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-9 text-sm',
  md: 'h-10 text-sm',
  lg: 'h-11 text-base',
};

export interface AutocompleteProps {
  /** Rotulo acima do campo; sem ele o campo aparece sozinho (filtros). */
  label?: string;
  placeholder?: string;
  size?: Size;
  variant?: Variant;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  error?: string;

  /** Selecao. Com `multiple`, a lista aceita varios; sem, no maximo um. */
  value: AutocompleteOption[];
  onChange: (options: AutocompleteOption[]) => void;
  multiple?: boolean;
  /** Texto quando nada esta escolhido, ex.: "Todas as unidades". */
  emptyLabel?: string;
  /** Quantas opcoes a lista mostra de uma vez - o resto aparece ao digitar. */
  limit?: number;

  /** Modo estatico: as opcoes ja estao na memoria. */
  options?: AutocompleteOption[];

  /** Modo remoto: busca no endpoint a cada pausa na digitacao. */
  url?: string;
  /** Nome do parametro de busca do endpoint (padrao `search`). */
  searchParam?: string;
  /** Filtros fixos que acompanham a busca, ex.: `{ status: 'active' }`. */
  params?: Record<string, string>;
  /** Converte a linha do endpoint em opcao (padrao: id + name). */
  mapOption?: (row: Record<string, unknown>) => AutocompleteOption;
}

/** Uma busca por pausa na digitacao, nao por tecla. */
const SEARCH_DELAY = 350;

const defaultMap = (row: Record<string, unknown>): AutocompleteOption => ({
  id: String(row.id),
  label: String(row.name ?? ''),
});

/**
 * Autocomplete unico do sistema: funciona com opcoes em memoria ou buscando
 * em um endpoint, com selecao simples ou multipla, navegacao por setas e
 * Enter para escolher.
 */
export function Autocomplete({
  label,
  placeholder,
  size = 'md',
  variant = 'field',
  className,
  inputClassName,
  disabled = false,
  error,
  value,
  onChange,
  multiple = false,
  emptyLabel,
  limit = 20,
  options,
  url,
  searchParam = 'search',
  params,
  mapOption = defaultMap,
}: AutocompleteProps) {
  const Icon = variant === 'filter' ? Filter : Search;
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const debouncedTerm = useDebounce(term, SEARCH_DELAY);

  const remote = useQuery({
    queryKey: ['autocomplete', url, params, debouncedTerm],
    enabled: Boolean(url) && open,
    queryFn: async () => {
      const query = new URLSearchParams({ ...params });
      if (debouncedTerm) query.set(searchParam, debouncedTerm);

      const response = await apiClient.get<{ data: Record<string, unknown>[] }>(
        `${url}?${query.toString()}`,
      );

      return response.data.map(mapOption);
    },
  });

  const available = useMemo(() => {
    const source = url ? (remote.data ?? []) : (options ?? []);
    const needle = term.trim().toLowerCase();

    // No modo remoto o endpoint ja filtrou; no estatico filtramos aqui.
    const filtered =
      url || !needle
        ? source
        : source.filter((option) => option.label.toLowerCase().includes(needle));

    const chosen = value.map((option) => option.id);
    return filtered.filter((option) => !chosen.includes(option.id)).slice(0, limit);
  }, [url, remote.data, options, term, value, limit]);

  useEffect(() => {
    setHighlighted(0);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function choose(option: AutocompleteOption) {
    onChange(multiple ? [...value, option] : [option]);
    setTerm('');
    setHighlighted(0);
    if (!multiple) setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlighted((current) => Math.min(current + 1, available.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      // Sempre segura o Enter: dentro de um formulario ele submeteria a tela.
      event.preventDefault();

      const option = available[highlighted];
      if (open && option) choose(option);
      return;
    }

    if (event.key === 'Escape') setOpen(false);
  }

  const single = !multiple ? (value[0] ?? null) : null;
  const fieldId = `autocomplete-${(label ?? placeholder ?? 'campo').toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className={cn('space-y-2', className)} ref={container}>
      {label ? <Label htmlFor={fieldId}>{label}</Label> : null}

      <div className="relative">
        {variant === 'search' || variant === 'filter' ? (
          <Icon className="-translate-y-1/2 absolute top-1/2 left-3 z-10 h-4 w-4 text-muted-foreground" />
        ) : null}

        <Input
          id={fieldId}
          autoComplete="off"
          disabled={disabled}
          placeholder={multiple && value.length > 0 ? '' : (emptyLabel ?? placeholder)}
          // Fechado mostra o escolhido; aberto, o que se esta digitando.
          value={open ? term : (single?.label ?? '')}
          className={cn(
            SIZE_CLASS[size],
            VARIANT_CLASS[variant],
            error && 'border-destructive',
            inputClassName,
          )}
          onFocus={() => {
            if (disabled) return;
            setTerm('');
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            setTerm(event.target.value);
            setHighlighted(0);
          }}
        />

        {open ? (
          <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-background py-1 shadow-lg">
            {url && remote.isFetching ? (
              <div className="flex justify-center py-3">
                <Spinner className="h-4 w-4 text-muted-foreground" />
              </div>
            ) : available.length > 0 ? (
              available.map((option, index) => (
                <button
                  type="button"
                  key={option.id}
                  className={cn(
                    'block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                    index === highlighted && 'bg-muted',
                  )}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => choose(option)}
                >
                  <span className="block truncate">{option.label}</span>
                  {option.hint ? (
                    typeof option.hint === 'string' ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.hint}
                      </span>
                    ) : (
                      <span className="mt-1 block">{option.hint}</span>
                    )
                  ) : null}
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-muted-foreground">Nada encontrado.</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Na selecao multipla os escolhidos viram etiquetas lado a lado: uma
          por linha, cada nome curto ocupava a largura toda do campo. */}
      {multiple && value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((option) => (
            <div
              key={option.id}
              className="flex max-w-full items-center gap-1.5 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-sm text-primary"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Check className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{option.label}</span>
              </span>
              <button
                type="button"
                aria-label={`Remover ${option.label}`}
                className="shrink-0 rounded p-0.5 hover:bg-primary/20"
                onClick={() => onChange(value.filter((entry) => entry.id !== option.id))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
